import { motion } from "framer-motion";
import {
  Waves, ArrowDownUp, Landmark, CalendarClock, FileSpreadsheet, ArrowUpRight,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";

const cardCls =
  "group relative overflow-hidden rounded-2xl border border-white/8 bg-[#0D111A] p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-emerald-400/40 hover:shadow-[0_24px_60px_rgba(0,255,135,0.08)]";

const CardIcon = ({ icon: Icon }) => (
  <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-400/20 bg-emerald-500/10">
    <Icon className="h-5 w-5 text-emerald-400" />
  </span>
);

const Spark = () => (
  <svg viewBox="0 0 320 90" className="mt-6 w-full" aria-hidden="true">
    <defs>
      <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#00FF87" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#00FF87" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M0 70 C30 62 45 40 70 44 C95 48 105 66 130 58 C155 50 165 24 190 26 C215 28 225 48 250 40 C275 32 295 16 320 12 L320 90 L0 90 Z"
      fill="url(#spark-fill)"
    />
    <motion.path
      d="M0 70 C30 62 45 40 70 44 C95 48 105 66 130 58 C155 50 165 24 190 26 C215 28 225 48 250 40 C275 32 295 16 320 12"
      fill="none"
      stroke="#00FF87"
      strokeWidth="2.5"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 2, ease: "easeOut", delay: 0.3 }}
    />
  </svg>
);

const BentoGrid = () => (
  <section id="ozellikler" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
      <Reveal className="max-w-2xl">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
          Beş Temel Güç
        </div>
        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
          Finans yönetiminin tüm parçaları, tek organizma.
        </h2>
      </Reveal>

      <div data-testid="bento-grid" className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-6">
        <Reveal className="md:col-span-6 lg:col-span-4" delay={0.05}>
          <div data-testid="bento-card-cashflow" className={`${cardCls} h-full`}>
            <div className="flex items-start justify-between">
              <CardIcon icon={Waves} />
              <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-3 py-1 text-xs font-medium text-emerald-400">
                <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Anlık
              </span>
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-100 sm:text-2xl">
              Anlık Nakit Akışı Takibi
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              Her tahsilat ve ödeme anında grafiğe işlenir. 30/60/90 günlük nakit
              projeksiyonu ile yarını bugünden gör.
            </p>
            <Spark />
          </div>
        </Reveal>

        <Reveal className="md:col-span-3 lg:col-span-2" delay={0.12}>
          <div data-testid="bento-card-expenses" className={`${cardCls} h-full`}>
            <CardIcon icon={ArrowDownUp} />
            <h3 className="font-display text-xl font-semibold text-slate-100">
              Gelir & Gider Analizi
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Kategori bazlı otomatik sınıflandırma, dönemsel karşılaştırma ve
              anomali uyarıları ile gider sızıntılarını yakala.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-3 lg:col-span-2" delay={0.18}>
          <div data-testid="bento-card-banks" className={`${cardCls} h-full`}>
            <CardIcon icon={Landmark} />
            <h3 className="font-display text-xl font-semibold text-slate-100">
              Banka & Cari Hesaplar
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Tüm banka hesapların ve cari hesap bakiyelerin tek ekranda,
              saniyeler içinde senkronize.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-3 lg:col-span-2" delay={0.24}>
          <div data-testid="bento-card-upcoming" className={`${cardCls} h-full`}>
            <CardIcon icon={CalendarClock} />
            <h3 className="font-display text-xl font-semibold text-slate-100">
              Yaklaşan Ödemeler & Tahsilatlar
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Vadesi gelen her kalem için akıllı hatırlatıcılar. Gecikme
              riski taşıyan tahsilatlar otomatik işaretlenir.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-6 lg:col-span-2" delay={0.3}>
          <div data-testid="bento-card-reports" className={`${cardCls} h-full`}>
            <CardIcon icon={FileSpreadsheet} />
            <h3 className="font-display text-xl font-semibold text-slate-100">
              Otomatik Finansal Raporlar
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Nakit akış tablosu, gelir-gider özeti ve yaşlandırma raporları
              tek tıkla PDF veya Excel olarak hazır.
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-400 transition-transform duration-300 group-hover:translate-x-1">
              Raporları keşfet <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default BentoGrid;
